import { useCallback } from 'react';
import type { Profession } from '@common/enums';
import {
    createParser,
    parseAsArrayOf,
    parseAsDate,
    parseAsString,
    useQueryState,
} from '@frontend/utils/hooks/useQueryState';
import type { OutpatientClinicTaskListFilter } from '../types/outpatientClinicTaskListFilter';

const parseAsObjectWithId = createParser<{ id: string }>({
    parse: (value: string | null) => (value ? { id: value } : undefined),
    serialize: (value: { id: string } | undefined) => (value ? value.id : null),
});

const parseAsProfession = createParser<Profession>({
    parse: (value: string | null) => (value ? (value as Profession) : undefined),
    serialize: (value: Profession | undefined) => value ?? null,
});

/**
 * Hook to manage filter state in URL parameters
 *
 * @returns Filter state and setter for outpatient clinic task list
 */
export function useFilterSearchParams(): [
    filter: OutpatientClinicTaskListFilter,
    setFilter: (filter: OutpatientClinicTaskListFilter) => void,
] {
    const [searchText, setSearchText] = useQueryState('searchText', parseAsString);
    const [startDate, setStartDate] = useQueryState('startDate', parseAsDate);
    const [endDate, setEndDate] = useQueryState('endDate', parseAsDate);
    const [type, setType] = useQueryState('type', parseAsArrayOf(parseAsObjectWithId));
    const [category, setCategory] = useQueryState('category', parseAsArrayOf(parseAsObjectWithId));
    const [episodeCategory, setEpisodeCategory] = useQueryState('episodeCategory', parseAsArrayOf(parseAsObjectWithId));
    const [performerPractitioner, setPerformerPractitioner] = useQueryState(
        'performer',
        parseAsArrayOf(parseAsObjectWithId)
    );
    const [performerPractitionerProfession, setPerformerPractitionerProfession] = useQueryState(
        'profession',
        parseAsArrayOf(parseAsProfession)
    );

    const filter: OutpatientClinicTaskListFilter = {
        category,
        endDate,
        episodeCategory,
        performerPractitioner,
        performerPractitionerProfession,
        searchText,
        startDate,
        type,
    };

    const setFilter = useCallback(
        (newFilter: OutpatientClinicTaskListFilter) => {
            setSearchText(newFilter.searchText);
            setStartDate(newFilter.startDate);
            setEndDate(newFilter.endDate);
            setType(newFilter.type);
            setCategory(newFilter.category);
            setEpisodeCategory(newFilter.episodeCategory);
            setPerformerPractitioner(
                Array.isArray(newFilter.performerPractitioner)
                    ? newFilter.performerPractitioner
                    : newFilter.performerPractitioner
                      ? [newFilter.performerPractitioner]
                      : undefined
            );
            setPerformerPractitionerProfession(
                Array.isArray(newFilter.performerPractitionerProfession)
                    ? newFilter.performerPractitionerProfession
                    : newFilter.performerPractitionerProfession
                      ? [newFilter.performerPractitionerProfession]
                      : undefined
            );
        },
        [
            setSearchText,
            setStartDate,
            setEndDate,
            setType,
            setEpisodeCategory,
            setPerformerPractitioner,
            setPerformerPractitionerProfession,
        ]
    );

    return [filter, setFilter];
}
